// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { usePlanDetailContext } from './PlanDetailContext';
import { EmptyState } from '../../../components/shell/ScreenHelpers';
import PlanTimeSeriesChart from '../../../components/charts/PlanTimeSeriesChart';
import PhaseBandLegend from '../../../components/charts/PhaseBandLegend';
import {
  NarrationCard,
  NarrationNote,
  SeriesLegend,
  accentFor,
  formatMagnitude,
  formatPercent,
  formatWithUnit,
  isFigure,
} from './PlanNarrationCard';
import {
  buildHourTicks,
  buildPhaseBandsOnAxis,
  formatAxisClock,
  getChartSpec,
} from '../../../utils/planSeries';
import { useSiteTime } from '../../../hooks/useSiteTime';
import type { PlanDriverFactor, PlanLoadImpact } from '../../../types';

/**
 * Impact headline as a percentage against baseline, matching how the narration
 * frames these ("−3% vs. baseline"). Falls back to an absolute delta when the
 * baseline is zero and a percentage would be meaningless, and to no headline at
 * all when either side is missing — a delta is a comparison, and with an operand
 * absent there is nothing to compare.
 */
function formatImpactDelta(impact: PlanLoadImpact): string | null {
  if (!isFigure(impact.baseline_value) || !isFigure(impact.recommended_value)) return null;
  const delta = impact.recommended_value - impact.baseline_value;
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  if (impact.baseline_value === 0) {
    return `${sign}${formatMagnitude(Math.abs(delta))} ${impact.unit} vs. baseline`;
  }
  const percent = (delta / Math.abs(impact.baseline_value)) * 100;
  return `${sign}${formatPercent(Math.abs(percent))}% vs. baseline`;
}

function PlanDriversTab() {
  const { planFile } = usePlanDetailContext();
  const drivers = planFile.drivers;
  const { siteZone } = useSiteTime();

  // Bands are projected onto the plan's own axis, which does not start at
  // midnight — see buildPhaseBandsOnAxis. No axis means no bands to place.
  const bands = useMemo(
    () =>
      planFile.raw_inputs
        ? buildPhaseBandsOnAxis(planFile.phases, planFile.raw_inputs.time_axis)
        : [],
    [planFile.phases, planFile.raw_inputs]
  );
  const hourTicks = useMemo(
    () => (planFile.raw_inputs ? buildHourTicks(planFile.raw_inputs.time_axis, 3, siteZone) : []),
    [planFile.raw_inputs, siteZone]
  );
  const timeAxis = planFile.raw_inputs?.time_axis;
  const hoverLabelAt = useMemo(
    () => (timeAxis ? (index: number) => formatAxisClock(timeAxis, index, siteZone) : undefined),
    [timeAxis, siteZone]
  );

  if (!drivers) {
    return <EmptyState big="No driver narration for this plan." />;
  }

  const renderFactor = (factor: PlanDriverFactor, index: number) => {
    const accent = accentFor(factor.key, index);
    const spec = getChartSpec(factor.key, planFile.raw_inputs);
    // No peak figure → no headline and no peak clause, rather than "— °C peak".
    const peak = isFigure(factor.peak_value)
      ? formatWithUnit(factor.peak_value, factor.peak_unit ?? '')
      : null;
    return (
      <NarrationCard
        key={factor.key}
        accent={accent}
        label={factor.label}
        headline={peak && `${peak} peak`}
        blocks={[
          { label: "What's driving this", text: factor.what_drives_it },
          { label: "Why it's worth acting on", text: factor.why_it_matters },
        ]}
        chart={
          spec && (
            <PlanTimeSeriesChart
              series={spec.series}
              unit={spec.unit}
              accent={accent}
              bands={bands}
              hourTicks={hourTicks}
              hoverLabelAt={hoverLabelAt}
              // Uses the same formatted string the headline shows, and drops
              // the clause entirely when there's no peak — "peaking at em
              // dash" is worse than silence.
              ariaLabel={
                peak
                  ? `${factor.label} over the day, peaking at ${peak}`
                  : `${factor.label} over the day`
              }
            />
          )
        }
      />
    );
  };

  const renderImpact = (impact: PlanLoadImpact, index: number) => {
    const accent = accentFor(impact.key, index);
    const spec = getChartSpec(impact.key, planFile.raw_inputs);
    const headline = formatImpactDelta(impact);
    const isComparison = (spec?.series.length ?? 0) > 1;
    return (
      <NarrationCard
        key={impact.key}
        accent={accent}
        label={impact.label}
        headline={headline}
        blocks={[
          { label: "What's driving this", text: impact.what_drives_it },
          { label: "Why it's worth acting on", text: impact.why_it_matters },
        ]}
        chart={
          spec && (
            <PlanTimeSeriesChart
              series={spec.series}
              unit={spec.unit}
              accent={accent}
              bands={bands}
              hourTicks={hourTicks}
              hoverLabelAt={hoverLabelAt}
              ariaLabel={`${impact.label}: baseline versus recommended over the day`}
              legend={isComparison ? <SeriesLegend accent={accent} /> : undefined}
            />
          )
        }
      />
    );
  };

  // Footnotes for conditions the plan explicitly did not act on. Surfacing
  // these is the point — silence would read as "we forgot to check".
  const notes: { key: string; accent: string; text: string }[] = [];
  if (drivers.demand_response_note) {
    notes.push({ key: 'dr', accent: 'var(--helios)', text: drivers.demand_response_note });
  }
  if (drivers.tariff && !drivers.tariff.considered && drivers.tariff.note) {
    notes.push({ key: 'tariff', accent: 'var(--text-helper)', text: drivers.tariff.note });
  }

  return (
    <div className="pd-stack">
      <p className="pd-narrative">{drivers.narrative}</p>

      <PhaseBandLegend bands={bands} />

      <div className="pd-columns">
        <section className="pd-column">
          <h3 className="pd-column-title">What&apos;s driving this</h3>
          {drivers.factors.length > 0 ? (
            drivers.factors.map(renderFactor)
          ) : (
            <EmptyState big="No driving factors listed." />
          )}
          {notes.length > 0 && (
            <div className="pd-notes">
              {notes.map(note => (
                <NarrationNote key={note.key} accent={note.accent} text={note.text} />
              ))}
            </div>
          )}
        </section>

        <section className="pd-column">
          <h3 className="pd-column-title">Load impact</h3>
          {drivers.load_impacts.length > 0 ? (
            drivers.load_impacts.map(renderImpact)
          ) : (
            <EmptyState big="No load impacts listed." />
          )}
        </section>
      </div>
    </div>
  );
}

export default PlanDriversTab;
